/* eslint-disable react/require-default-props */
import React, { useCallback, useContext } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body1,
  Body1Strong,
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  mergeClasses,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { StackedDrawer } from '@bingads-webui-campaign-react/drawer-stack';

import { useHelpResourcesDrawerStyles } from './use-help-resources-drawer-styles';
import { HELP_RESOURCE_CONTENT, type HelpResourceOption } from './help-resources-content';

export interface HelpResourcesDetailDrawerProps {
  option: HelpResourceOption | null;
  onClose: () => void;
  logger?: {
    // eslint-disable-next-line no-unused-vars
    trace: (message: string) => void;
  };
}

// Second-layer (stacked) drawer that lists the question / answer / "what to do" entries for the
// option the user picked in the main "Help resources" drawer.
export const HelpResourcesDetailDrawer: React.FC<HelpResourcesDetailDrawerProps> = ({
  option,
  onClose,
  logger,
}) => {
  const i18n = useContext(I18nContext);
  const styles = useHelpResourcesDrawerStyles();

  const handleClose = useCallback(() => {
    logger?.trace('HelpResourcesDetailDrawer: Closed detail drawer');
    onClose();
  }, [onClose, logger]);

  if (!option) {
    return null;
  }

  const content = HELP_RESOURCE_CONTENT[option];

  return (
    <StackedDrawer
      className="help-resources-detail-drawer"
      name="help-resources-detail"
      onOpenChange={(_, { open: isOpen }) => {
        if (!isOpen) {
          handleClose();
        }
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={i18n.getString(_TL_('Close'))}
              icon={<Dismiss24Regular />}
              onClick={handleClose}
            />
          }
        >
          {i18n.getString(content.title)}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={mergeClasses('help-resources-detail-body', styles.body)}>
        <Accordion
          className={styles.detailList}
          multiple
          collapsible
          onToggle={(_, data) => {
            logger?.trace(`HelpResourcesDetailDrawer: Toggled accordion option=${option} question="${data.value}"`);
          }}
        >
          {content.items.map(item => (
            <AccordionItem
              key={item.question}
              value={item.question}
              className={mergeClasses('help-resources-detail-item', styles.detailItem)}
            >
              <AccordionHeader expandIconPosition="end" size="small">
                <Body1Strong className={styles.detailTitle}>{i18n.getString(item.question)}</Body1Strong>
              </AccordionHeader>
              <AccordionPanel className={styles.detailPanel}>
                <Body1 className={styles.detailDescription}>{i18n.getString(item.description)}</Body1>
                <Body1Strong className={styles.detailWhatToDoLabel}>{i18n.getString(_TL_('What to do'))}</Body1Strong>
                <Body1 className={styles.detailWhatToDo}>
                  <ReactTemplate
                    template={i18n.getString(item.whatToDo)}
                    model={{
                      strong: (text: string) => <Body1Strong>{text}</Body1Strong>,
                    }}
                  />
                </Body1>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </DrawerBody>
    </StackedDrawer>
  );
};
